import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Loader2, Eye, EyeOff, Clock, AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import { DevLink } from "@/pages/AuthActionPages";

const BG =
  "https://images.unsplash.com/photo-1695721780267-9ce4a448ef05?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGRhcmslMjBsdXh1cnklMjB0ZXh0dXJlJTIwZ29sZCUyMHNsYXRlfGVufDB8fHx8MTc4MTYzMDAyOXww&ixlib=rb-4.1.0&q=85";

export default function LoginPage() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (user) navigate("/");
  }, [user, navigate]);

  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("expired")) {
      setError("A sessão expirou. Inicie sessão novamente.");
    }
  }, []);

  const [blocked, setBlocked] = useState(null); // {code, message}
  const [resend, setResend] = useState(null);

  const resendVerification = async () => {
    try {
      const { data } = await api.post("/auth/resend-verification", { email: email.trim() });
      setResend({ msg: data.message, link: data.dev_link });
    } catch (e) {
      setResend({ msg: "Não foi possível reenviar. Tente mais tarde." });
    }
  };

  const extractError = (err) => {
    const d = err?.response?.data?.detail;
    if (d && typeof d === "object" && d.code) {
      setBlocked(d);
      return "";
    }
    if (typeof d === "string") return d;
    if (Array.isArray(d)) return "Verifique o email e a palavra-passe e tente novamente.";
    if (err?.response?.status === 401) return "Email ou palavra-passe incorretos";
    if (!err?.response) return "Sem ligação ao servidor. Verifique a Internet e tente novamente.";
    return "Não foi possível iniciar sessão. Tente novamente.";
  };

  const doLogin = async (em, pw) => {
    setError("");
    setBlocked(null);
    setResend(null);
    setBusy(true);
    try {
      await login((em || "").trim(), pw);
      navigate("/");
    } catch (err) {
      setError(extractError(err));
    } finally {
      setBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    await doLogin(email, password);
  };

  const quickLogin = async (em, pw) => {
    setEmail(em);
    setPassword(pw);
    await doLogin(em, pw);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#09090b]">
      {/* Left visual */}
      <div className="relative hidden lg:block">
        <img src={BG} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/55" />
        <div className="relative h-full flex flex-col justify-between p-12">
          <div className="flex items-center gap-3">
            <img src="/radarx-logo.png" alt="RadarX" className="h-10 w-auto" />
          </div>
          <div>
            <h1 className="font-head text-4xl sm:text-5xl font-light text-[#fafafa] leading-tight">
              Painel de <span className="text-[#c8aa6e]">Inteligência</span>
              <br /> de Anúncios
            </h1>
            <p className="mt-4 text-sm text-[#a1a1aa] max-w-md leading-relaxed">
              Monitorize diariamente novos perfis em múltiplos sites, detete números
              duplicados e acompanhe estatísticas num único lugar.
            </p>
          </div>
          <div className="label-tiny">Acesso restrito · Sessão encriptada</div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-sm fade-up" data-testid="login-form">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <img src="/radarx-logo.png" alt="RadarX" className="h-10 w-auto" />
          </div>
          <h2 className="font-head text-2xl font-normal text-[#fafafa]">Iniciar Sessão</h2>
          <p className="text-sm text-[#71717a] mt-1 mb-8">Introduza as suas credenciais</p>

          <label className="label-tiny block mb-2">Email</label>
          <input
            data-testid="login-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full bg-[#09090b] border border-[#3f3f46] text-[#fafafa] placeholder-[#71717a] focus:border-[#c8aa6e] focus:ring-1 focus:ring-[#c8aa6e] outline-none px-3 py-2.5 text-sm mb-5 transition-colors"
            placeholder="admin@agregador.pt"
            required
          />

          <label className="label-tiny block mb-2">Palavra-passe</label>
          <div className="relative mb-6">
            <input
              data-testid="login-password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-[#09090b] border border-[#3f3f46] text-[#fafafa] placeholder-[#71717a] focus:border-[#c8aa6e] focus:ring-1 focus:ring-[#c8aa6e] outline-none px-3 py-2.5 pr-10 text-sm transition-colors"
              placeholder="••••••••"
              required
            />
            <button
              type="button"
              data-testid="toggle-password"
              onClick={() => setShowPassword((s) => !s)}
              aria-label={showPassword ? "Ocultar palavra-passe" : "Mostrar palavra-passe"}
              className="absolute inset-y-0 right-0 flex items-center px-3 text-[#71717a] hover:text-[#c8aa6e] transition-colors"
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>

          {error && (
            <div data-testid="login-error" className="text-[#ef4444] text-sm mb-4 border border-[#ef4444]/30 bg-[#ef4444]/5 px-3 py-2">
              {error}
            </div>
          )}
          {blocked && (
            <div data-testid={`login-blocked-${blocked.code}`} className="text-sm mb-4 border border-[#c8aa6e]/40 bg-[#c8aa6e]/5 px-3 py-3 text-[#e4e4e7] flex gap-2">
              {blocked.code === "not_approved" ? <Clock className="w-4 h-4 text-[#c8aa6e] shrink-0 mt-0.5" /> : <AlertTriangle className="w-4 h-4 text-[#c8aa6e] shrink-0 mt-0.5" />}
              <div className="flex-1">
                <div>{blocked.message}</div>
                {blocked.code === "email_unverified" && !resend && (
                  <button type="button" onClick={resendVerification} data-testid="login-resend-verification" className="mt-2 text-xs text-[#c8aa6e] hover:text-[#d4b982] underline">
                    Reenviar e-mail de confirmação
                  </button>
                )}
                {resend && (
                  <div className="mt-2 text-xs text-[#a1a1aa]" data-testid="login-resend-result">
                    {resend.msg}
                    <DevLink link={resend.link} />
                  </div>
                )}
              </div>
            </div>
          )}

          <button
            data-testid="login-submit"
            type="submit"
            disabled={busy}
            className="w-full bg-[#c8aa6e] text-black hover:bg-[#d4b982] active:scale-[0.98] px-4 py-2.5 font-medium text-sm transition-all flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {busy && <Loader2 className="w-4 h-4 spin" />}
            Entrar
          </button>

          <p className="text-sm text-[#71717a] mt-4 text-center">
            <a href="/forgot-password" data-testid="go-forgot" className="text-[#a1a1aa] hover:text-[#c8aa6e]">Esqueci-me da palavra-passe</a>
          </p>
          <p className="text-sm text-[#71717a] mt-3 text-center">
            Ainda não tens conta?{" "}
            <a href="/register" data-testid="go-register" className="text-[#c8aa6e] hover:text-[#d4b982]">
              Criar conta
            </a>
          </p>

          <div className="mt-6 pt-5 border-t border-[#27272a]">
            <p className="label-tiny text-center mb-3">Acesso rápido (demo)</p>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                data-testid="quick-admin"
                onClick={() => quickLogin("admin@agregador.pt", "Admin123!")}
                disabled={busy}
                className="border border-[#3f3f46] text-[#fafafa] hover:border-[#c8aa6e] hover:text-[#c8aa6e] px-3 py-2 text-sm transition-colors disabled:opacity-60"
              >
                Entrar como Admin
              </button>
              <button
                type="button"
                data-testid="quick-client"
                onClick={() => quickLogin("cliente@teste.pt", "teste123")}
                disabled={busy}
                className="border border-[#3f3f46] text-[#fafafa] hover:border-[#c8aa6e] hover:text-[#c8aa6e] px-3 py-2 text-sm transition-colors disabled:opacity-60"
              >
                Entrar como Cliente
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
